import { useMemo } from "react";
import { Link } from "wouter";
import { useListRecipes, useListRecipeCategories } from "@workspace/api-client-react";
import { Layout } from "@/components/Layout";
import { ChevronRight, FolderOpen } from "lucide-react";
import { cn } from "@/lib/utils";

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  "main dish":       { bg: "hsl(17 53% 51%)",  text: "hsl(40 33% 97%)" },
  "soup/stew":       { bg: "hsl(25 80% 45%)",  text: "hsl(40 33% 97%)" },
  bread:             { bg: "hsl(45 75% 55%)",  text: "hsl(33 28% 14%)" },
  dessert:           { bg: "hsl(350 55% 75%)", text: "hsl(33 28% 14%)" },
  beverage:          { bg: "hsl(210 50% 60%)", text: "hsl(40 33% 97%)" },
  salad:             { bg: "hsl(145 40% 45%)", text: "hsl(40 33% 97%)" },
  appetizer:         { bg: "hsl(38 85% 55%)",  text: "hsl(33 28% 14%)" },
  side:              { bg: "hsl(90 30% 50%)",  text: "hsl(40 33% 97%)" },
  breakfast:         { bg: "hsl(50 90% 60%)",  text: "hsl(33 28% 14%)" },
  snack:             { bg: "hsl(38 85% 55%)",  text: "hsl(33 28% 14%)" },
  sauce:             { bg: "hsl(91 18% 36%)",  text: "hsl(40 33% 97%)" },
  "sauce/condiment": { bg: "hsl(91 18% 36%)", text: "hsl(40 33% 97%)" },
};
const DEFAULT_COLOR = { bg: "hsl(30 15% 55%)", text: "hsl(40 33% 97%)" };

function getCategoryColor(category: string) {
  const key = category.toLowerCase().trim();
  if (CATEGORY_COLORS[key]) return CATEGORY_COLORS[key];
  for (const [k, v] of Object.entries(CATEGORY_COLORS)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return DEFAULT_COLOR;
}

interface CategoryCardProps {
  name: string;
  count: number;
  heroImages: string[];
}

function CategoryCard({ name, count, heroImages }: CategoryCardProps) {
  const { bg, text } = getCategoryColor(name);

  const slots = [0, 1, 2, 3];

  return (
    <Link href={`/?category=${encodeURIComponent(name)}`}>
      <article
        className={cn(
          "group bg-white rounded-2xl overflow-hidden border border-border/50 shadow-sm cursor-pointer",
          "transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-primary/30"
        )}
      >
        {/* 2×2 image collage */}
        <div className="relative aspect-square overflow-hidden">
          <div className="grid grid-cols-2 grid-rows-2 w-full h-full">
            {slots.map((i) => {
              const imgUrl = heroImages[i];
              return (
                <div key={i} className="relative overflow-hidden">
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        const img = e.currentTarget;
                        img.style.display = "none";
                        const fb = img.nextElementSibling as HTMLElement | null;
                        if (fb) fb.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className={cn(
                      "w-full h-full items-center justify-center",
                      imgUrl ? "hidden" : "flex"
                    )}
                    style={{ backgroundColor: bg }}
                  />
                </div>
              );
            })}
          </div>

          {/* thin gap lines between the 4 quadrants */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-white/60 -translate-y-px" />
            <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-white/60 -translate-x-px" />
          </div>

          {/* count badge top-right */}
          <div className="absolute top-2.5 right-2.5">
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full shadow-sm"
              style={{ backgroundColor: bg, color: text }}
            >
              {count}
            </span>
          </div>
        </div>

        {/* label */}
        <div className="p-3 flex items-center justify-between gap-2">
          <div>
            <p
              className="font-semibold text-sm text-foreground leading-tight group-hover:text-primary transition-colors"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {name}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {count} recipe{count !== 1 ? "s" : ""}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary transition-colors flex-shrink-0" />
        </div>
      </article>
    </Link>
  );
}

export default function CategoriesPage() {
  const { data: recipesData, isLoading: recipesLoading } = useListRecipes();
  const { data: categoriesData, isLoading: categoriesLoading } = useListRecipeCategories();

  const recipes = recipesData?.recipes ?? [];
  const apiCategories = categoriesData?.categories ?? [];

  const isLoading = recipesLoading || categoriesLoading;

  const categoryCards = useMemo(() => {
    const heroMap = new Map<string, string[]>();
    for (const r of recipes) {
      if (!r.category) continue;
      const key = r.category;
      if (!heroMap.has(key)) heroMap.set(key, []);
      if (r.heroImagePath) {
        const imgs = heroMap.get(key)!;
        if (imgs.length < 4) {
          imgs.push(`/api/recipes/hero-images/file/${r.id}-hero.jpg`);
        }
      }
    }

    return apiCategories
      .slice()
      .sort((a, b) => b.count - a.count)
      .map((cat) => ({
        name: cat.category,
        count: cat.count,
        heroImages: heroMap.get(cat.category) ?? [],
      }));
  }, [recipes, apiCategories]);

  const totalRecipes = apiCategories.reduce((s, c) => s + c.count, 0);

  return (
    <Layout>
      <div className="space-y-8 max-w-5xl mx-auto px-1">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Categories</span>
        </div>

        {/* Header */}
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold text-foreground mb-2"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Recipe Categories
          </h1>
          <p className="text-muted-foreground">
            {isLoading
              ? "Loading…"
              : `${totalRecipes} recipes across ${categoryCards.length} categories`}
          </p>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-muted animate-pulse">
                <div className="aspect-square rounded-t-2xl bg-muted-foreground/10" />
                <div className="p-3 space-y-2">
                  <div className="h-4 w-2/3 rounded-full bg-muted-foreground/10" />
                  <div className="h-3 w-1/3 rounded-full bg-muted-foreground/10" />
                </div>
              </div>
            ))}
          </div>
        ) : categoryCards.length === 0 ? (
          <div className="rounded-3xl border border-border/50 bg-white shadow-sm px-8 py-14 text-center space-y-4 max-w-md mx-auto">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FolderOpen className="w-7 h-7 text-primary" />
            </div>
            <h2
              className="text-xl font-bold text-foreground"
              style={{ fontFamily: "var(--font-display)" }}
            >
              No categories yet
            </h2>
            <p className="text-muted-foreground text-sm">
              Categories appear automatically as you add recipes. Add a recipe to get started.
            </p>
            <Link
              href="/"
              className="inline-flex items-center justify-center rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 active:scale-95"
            >
              Back to cookbook
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categoryCards.map(({ name, count, heroImages }) => (
              <CategoryCard
                key={name}
                name={name}
                count={count}
                heroImages={heroImages}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
